import type { CityListingPages } from '@todo/core/city-listings';
import { prefetchIndexLists } from '@/lib/detail/list-prefetch';
import { EVENT_SHOW_BUCKET, fetchEventShows } from '@todo/core/detail/event-detail';
import type { EventShow } from '@todo/core/event-show';
import { DEFAULT_LOCALE, isSupportedLocale } from '@todo/core/i18n/locales';
import { CITY_LISTING_PAGES_SEED_KEY, LIST_BUCKET } from '@todo/core/list-queries';
import { getPrefetched, setPrefetched } from '@todo/core/static-prefetch';
import { supabase } from '@todo/core/supabase';

// Shared by the native and web variants of app/[lang]/events/[id] so the
// static-export data fetching lives in exactly one place: the shows from
// @todo/core's fetchEventShows (shared with dubai), plus the old per-date
// redirect pages only riviera has. Fetched once per export process (not once
// per locale) — generateStaticParams is called for every cascaded {lang}, and
// events don't vary by locale.
/**
 * Prefetch bucket for an old per-date URL's redirect target, keyed by row id →
 * the target's path under /[lang] (`events/<show_slug>`, `<city>/events` or
 * `events`).
 */
export const EVENT_REDIRECT_BUCKET = 'event-redirect';

/**
 * Rows created before shows replaced per-date pages (2026-10-02) were
 * published at /[lang]/events/<uuid>; only those get a redirect page. They're
 * read from `event_legacy_urls` (20261021120000_event_legacy_urls.sql), a
 * frozen copy that archive_past_events() never purges, so a redirect outlives
 * its row: Google advises keeping a redirect for a year. The table can't grow
 * — a row created later never had a URL of its own.
 */
type LegacyUrl = { id: string; show_slug: string; city_id: string | null };

/**
 * Where an old per-date URL points: its show while that is exported, else the
 * city's /[city]/events page if it has one, else the /events hub — never a
 * page the export didn't write.
 */
function legacyTarget(row: LegacyUrl, shows: Map<string, EventShow>, cityPages: string[]): string {
  if (shows.has(row.show_slug)) return `events/${row.show_slug}`;
  if (row.city_id && cityPages.includes(row.city_id)) return `${row.city_id}/events`;
  return 'events';
}

type ExportData = { shows: Map<string, EventShow>; redirects: Map<string, string> };

let exportPromise: Promise<ExportData> | null = null;

function fetchExportData(): Promise<ExportData> {
  if (!exportPromise) {
    exportPromise = Promise.all([
      fetchEventShows(),
      Promise.resolve(supabase.from('event_legacy_urls').select('id,show_slug,city_id')),
      // The /[city]/events page list, to send a redirect whose show is gone
      // only to a page that was exported (already awaited by
      // app/[lang]/_layout.tsx; memoized).
      prefetchIndexLists(),
    ]).then(([shows, old]) => {
      if (old.error) throw new Error(`generateStaticParams(event redirects): ${old.error.message}`);

      const cityPages =
        getPrefetched<CityListingPages>(LIST_BUCKET, CITY_LISTING_PAGES_SEED_KEY)?.events ?? [];
      const redirects = new Map<string, string>();
      for (const row of (old.data ?? []) as LegacyUrl[]) {
        redirects.set(row.id, legacyTarget(row, shows, cityPages));
      }
      return { shows, redirects };
    });
  }
  return exportPromise;
}

// Enumerate every show so the static export emits one HTML file per
// {lang, show}, plus a redirect page per old per-date URL, and prime the build
// cache so each file ships real <Head> meta + content.
// Argument shape: expo-router passes `{ params: parentParams }`, not the
// params directly — see the note in article-detail.ts for what getting this
// wrong cost there. Events don't vary by locale, so only the
// returned `lang` matters here.
export async function generateEventStaticParams({
  params,
}: {
  params: { lang?: string };
}): Promise<{ lang: string; id: string }[]> {
  const { shows, redirects } = await fetchExportData();
  for (const [slug, show] of shows) setPrefetched(EVENT_SHOW_BUCKET, slug, show);
  for (const [id, path] of redirects) setPrefetched(EVENT_REDIRECT_BUCKET, id, path);
  // Fall back the same way article-detail.ts does, so a missing parent param
  // can never silently produce a route with an undefined locale segment.
  const lang = isSupportedLocale(params.lang) ? params.lang : DEFAULT_LOCALE;
  return [
    ...[...shows.keys()].map((id) => ({ lang, id })),
    ...[...redirects.keys()].map((id) => ({ lang, id })),
  ];
}
